import { useState, useEffect } from 'react';
import {
    Boolean, Calculation, Contact, ContactList, DateInput, Deadline, Dropdown,
    FileUpload, Instructions, MultiFile, MultiSelect, NumberInput, Subheader, 
    Text, TimeInput, SaveButton, SearchSelect
} from "./FieldComponents";
import { MultiVal } from './MultiVal';
import Alert from './Alert';
import MatterPublicFormField from './MatterPublicFormField';

const normalizeValueToIndex = (field, value) => {
    if (!field.options) return value;

    let options = [];
    try {
        options = Array.isArray(field.options)
            ? field.options
            : JSON.parse(field.options);
    } catch {
        return value;
    }

    if (typeof value === "string") {
        const index = options.findIndex(opt => opt.toLowerCase?.() === value.toLowerCase?.());
        return index !== -1 ? index : value;
    }

    return value;
};

const NotesField = ({ fieldId, value, onChange }) => {
    return (
        <div className="form-group nm conditional">
            <label className="subtext">Additional Notes</label>
            <Text 
                type="textarea" 
                placeholder="Enter additional notes here..." 
                value={value || ''} 
                onChange={onChange}
            />
        </div>
    );
};

const Field = ({ field, value, handleFieldChange, conditional = false, formData, fieldUpdates, fields, caseId, leadId, refreshAfterCalc = null, handleNotesChange, notesData }) => {
    if (!field) return null;

    const hasBooleanYesNo = (field.field_id === 10 && field.options && 
        (Array.isArray(field.options) 
            ? field.options.includes('Yes') && field.options.includes('No')
            : String(field.options).includes('Yes') && String(field.options).includes('No'))) || field.id === 507;

    const renderFieldInput = (field) => {
        switch (field.field_id) {
            case 1:
                return <Text type="text" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 2:
                return <Text type="textarea" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 3:
                return <NumberInput type="currency" value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 4:
                return <NumberInput type="percent" value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 5:
                return <NumberInput type="number" value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 6:
                return <DateInput value={String(value)} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 7:
                return <TimeInput value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 8:
                return <Contact selectedContact={value} setSelectedContact={(contact) => handleFieldChange(field.id, contact?.id || null)} />;
            case 9:
                return <ContactList value={Array.isArray(value) ? value : []} onChange={(ids) => handleFieldChange(field.id, ids)} />;
            case 10:
                return <Dropdown 
                        options={field.options || "[]"} 
                        value={value} 
                        onChange={(index) => handleFieldChange(field.id, index)} 
                        marketing_list={field.id === 225} 
                    />;
            case 11:
                return <MultiSelect options={field.options || "[]"} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 12:
                return <Boolean options={field.options || []} value={value != undefined ? Number(value) : 2} onChange={(selectedValue) => handleFieldChange(field.id, selectedValue)} />;
            case 13:
                return <Subheader title={field.name} />;
            case 14:
                return <Instructions instructions={field.name}/>;
            case 15:
                return <FileUpload value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 16:
                return <MultiFile value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 17:
                return (
                    <Calculation
                        options={field.options}
                        fieldId={field.id}
                        case_id={caseId}
                        fields={fields}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        onChange={(val) => {
                            if (typeof refreshAfterCalc === 'function') {
                                refreshAfterCalc(field.id, val);
                            } else {
                                console.error('refreshAfterCalc is not a function:', typeof refreshAfterCalc);
                                handleFieldChange(field.id, val);
                            }
                        }}
                    />
                );                
            case 18:
                return <SearchSelect value={value} onChange={(val) => handleFieldChange(field.id, val)} options={field.options} />;
            case 20:
                const parsedValue = typeof value === 'string' ? JSON.parse(value || '{}') : value || {};
                return <Deadline 
                            value={{ due: parsedValue.due || '', done: parsedValue.done || '' }}
                            title={field.name}
                            onChange={(updated) => handleFieldChange(field.id, {
                                ...parsedValue,
                                ...updated
                            })}
                        />;
            case 22:
                return <SearchSelect
                            value={value}
                            onChange={(val) => handleFieldChange(field.id, val)}
                            options={field.options}
                        />;
            case 26:
                return <MultiVal
                            value={value}
                            options={field.options || '[]'}
                            fieldName={field.name}
                            onChange={(val) => handleFieldChange(field.id, val)}
                        />;
            case 28:
                return <MatterPublicFormField
                            field={field}
                            leadId={leadId}
                        />;
            default:
                return null;
        }
    };

    return (
        <>
            {![13, 14, 20, 26].includes(field.field_id) ? (
                <div className={`form-group nm ${conditional === false ? '' : 'conditional'}`}>
                    <label className='subtext'>{field.name}</label>
                    {renderFieldInput(field)}
                </div>
            ) : renderFieldInput(field)}
            
            {hasBooleanYesNo && (
                <NotesField 
                    fieldId={field.id} 
                    value={notesData[field.id] || ''} 
                    onChange={(val) => handleNotesChange(field.id, val)} 
                />
            )}
        </>
    );
};

export const LeadPFS = ({ leadData, fetchCustomFields }) => {
    const [fields, setFields] = useState([]);
    const [fieldUpdates, setFieldUpdates] = useState({});
    const [formData, setFormData] = useState({});
    const [dataChanged, setDataChanged] = useState(false);
    const [notesData, setNotesData] = useState({});
    const [notesChanged, setNotesChanged] = useState(false);
    const [sectionName, setSectionName] = useState('PFS Fields');
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);

    useEffect(() => {
        if (leadData?.id) {
            fetchFields(leadData.id);
            fetchNotes(leadData.id);
        }
    }, [leadData?.id]);

    const fetchFields = async (leadId) => {
        setLoading(true);
        try {
            const res = await fetch(
                `https://api.casedb.co/custom_fields.php?section_id=47&template_id=7&lead_id=${leadId}&${Date.now()}`,
                { credentials: 'include' }
            );
            const data = await res.json();
            
            const sortedFields = (data.custom_fields || []).sort((a, b) => a.order_id - b.order_id);
            setFields(sortedFields);
            setSectionName(data.section_name || "PFS Fields");
            mergeFieldValues(sortedFields, data.field_updates || []);
        } catch (error) {
            console.error("Error fetching PFS fields:", error);
        } finally {
            setLoading(false);
        }
    };

    const mergeFieldValues = (customFields, updates) => {
        const flatMap = {};
        
        updates.forEach(update => {
            if (update.group_id === 0) {
                Object.entries(update).forEach(([key, val]) => {
                    if (key !== 'group_id') {
                        const fieldDef = customFields.find(f => String(f.id) === String(key));
                
                        if (String(val) === String(key)) return;
                
                        if (fieldDef?.field_id === 17) {
                            const num = parseFloat(val);
                            flatMap[key] = isNaN(num) ? 0 : parseFloat(num.toFixed(2));
                        } else {
                            flatMap[key] = val;
                        }
                    }
                });
            }
        });
    
        setFieldUpdates(flatMap);
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
        setDataChanged(true);
    };

    const refreshAfterCalc = (fieldId, value) => {
        handleFieldChange(fieldId, value);
    };

    const fetchNotes = async (leadId) => {
        try {
            const response = await fetch(`https://api.casedb.co/pfs_notes.php?lead_id=${leadId}`);
            const data = await response.json();
            
            if (data.success && data.notes) {
                const notesObj = {};
                data.notes.forEach(note => {
                    notesObj[note.field_id] = note.data;
                });
                setNotesData(notesObj);
            }
        } catch (error) {
            console.error("Error fetching PFS notes:", error);
        }
    };

    const handleNotesChange = (fieldId, value) => {
        setNotesData((prev) => ({ ...prev, [fieldId]: value }));
        setNotesChanged(true);
    };

    const saveFields = async () => {
        if (!leadData?.id) return;
        
        const data = {
            lead_id: leadData.id,
            field_values: Object.entries(formData).reduce((acc, [id, value]) => {
                acc[id] = (typeof value === 'object') ? JSON.stringify(value) : value;
                return acc;
            }, {})
        };
        
        try {
            await fetch('https://api.casedb.co/leads.php', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    id: leadData.id,
                    pfs: 1
                })
            });
            
            const response = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(data)
            });
            
            const result = await response.json();
            
            if (notesChanged) {
                const notesPayload = {
                    lead_id: leadData.id,
                    notes: Object.entries(notesData).map(([field_id, data]) => ({
                        field_id,
                        data
                    }))
                };
                
                const notesResponse = await fetch('https://api.casedb.co/pfs_notes.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(notesPayload)
                });
                
                const notesResult = await notesResponse.json();
                if (!notesResult.success) {
                    console.error("Failed to update notes:", notesResult.message);
                }
            }
            
            if (result.success) {
                setDataChanged(false);
                setNotesChanged(false);
                fetchFields(leadData.id);
                fetchNotes(leadData.id);
                setAlertMessage({ message: "Fields updated successfully!", type: "success" });
                if (fetchCustomFields) {
                    fetchCustomFields();
                }
            } else {
                console.error("Failed to update fields:", result.message);
                setAlertMessage({ message: "Failed to update fields!", type: "error" });
            }
        } catch (error) {
            console.error("Error updating fields:", error);
            setAlertMessage({ message: "Failed to update fields!", type: "error" });
        }
    };

    const resolveCalculationValue = (fieldId, visited = new Set()) => {
        if (visited.has(fieldId)) {
            console.warn(`Circular dependency detected for field ID: ${fieldId}`);
            return 0;
        }
    
        visited.add(fieldId);
    
        const field = fields.find(f => f.id === fieldId);
        if (!field || field.field_id !== 17) return getFieldValue(field);
    
        const options = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
        const dependentFieldIds = JSON.parse(options?.field_ids || '[]');
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        let expression = options?.rule || '';
    
        dependentFieldIds.forEach((id, idx) => {
            let value = resolveCalculationValue(id, visited);
            if (value === '' || value === null || value === undefined) {
                const operatorBefore = expression[expression.indexOf(letters[idx]) - 1];
                value = operatorBefore === '*' || operatorBefore === '/' ? 1 : 0;
            }
            expression = expression.replaceAll(letters[idx], value);
        });
    
        try {
            if (!expression || /[^0-9+\-*/(). ]/.test(expression) || /[+\-*/]$/.test(expression)) {
                throw new SyntaxError(`Invalid or incomplete expression: "${expression}"`);
            }
    
            const result = new Function(`return ${expression}`)();
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : 0;
        } catch (e) {
            console.error(`Error evaluating calculation for field ID: ${fieldId}`, e, { expression });
            return 0;
        }
    };
    
    const getFieldValue = (field) => {
        if (!field) return '';
        
        const val = formData[field.id];
        if (val !== undefined && val !== null) return val;

        const stored = fieldUpdates?.[field.id];
        if (stored !== undefined) return stored;

        if (field.field_id === 17) {
            return resolveCalculationValue(field.id);
        }

        if (field.field_id === 12) return 2;

        return '';
    };

    const shouldDisplay = (field) => {
        if (Number(field.hidden) === 2 || Number(field.obsolete) === 2) return false;
    
        // Parse display_when/display_multi (check display_multi first)
        let fieldIdToCheck = null;
        try {
            const toparse = field.display_multi || field.display_when;
            const parsed = JSON.parse(String(toparse));
            if (Array.isArray(parsed)) {
                fieldIdToCheck = parsed[0];
            } else {
                fieldIdToCheck = parseInt(parsed);
            }
        } catch (e) {
            fieldIdToCheck = parseInt(field.display_multi || field.display_when);
        }
        
        const controllingField = fields.find(f => f.id === fieldIdToCheck);
        if (!field.display_when && !field.display_multi || !controllingField) return true;
    
        let controllingValue = getFieldValue(controllingField);

        if (field.is_answered === '[!]') {
            return controllingValue === null || controllingValue === undefined || controllingValue === "";
        }
    
        if (field.is_answered === null || field.is_answered === "") {
            return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
        }
    
        controllingValue = normalizeValueToIndex(controllingField, controllingValue);
    
        let parsedAnswers = [];
        try {
            const parsed = JSON.parse(field.is_answered);
            if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
            else parsedAnswers = [String(parsed)];
        } catch {
            parsedAnswers = String(field.is_answered).split(',').map(v => v.trim());
        }
        
        const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
        const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

        if (isNegated) {
            return !cleanAnswers.includes(String(controllingValue));
        } else {
            return cleanAnswers.includes(String(controllingValue));
        }
    };

    const renderFields = () => {
        const dependencyMap = {};
        fields.forEach(field => {
            if (field.display_when) {
                if (!dependencyMap[field.display_when]) dependencyMap[field.display_when] = [];
                dependencyMap[field.display_when].push(field);
            }
        });

        const rendered = new Set();
        const output = [];
        let currentSubSection = [];
        let currentInstructionGroup = [];

        const flushSubSection = () => {
            if (currentSubSection.length > 0) {
                if (currentSubSection[0].props.field.field_id === 13) {
                    output.push(
                        <div key={`frag-${output.length}`}>
                            {currentSubSection}
                        </div>
                    );
                } else {
                    output.push(
                        <div className="sub-section" key={`sub-${output.length}`}>
                            {currentSubSection}
                        </div>
                    );
                }
                currentSubSection = [];
            }
        };

        const flushInstructionGroup = () => {
            if (currentInstructionGroup.length > 0) {
                output.push(
                    <div className="instruction-group" key={`instruction-${output.length}`}>
                        {currentInstructionGroup}
                    </div>
                );
                currentInstructionGroup = [];
            }
        };

        fields
            .filter(f => shouldDisplay(f))
            .sort((a, b) => a.order_id - b.order_id)
            .forEach((field, index) => {
                if (rendered.has(field.id)) return;

                const children = dependencyMap[field.id] || [];
                const visibleChildren = children.filter(shouldDisplay);
                
                const fieldEl = (
                    <Field
                        key={`${field.id}-${index}`}
                        field={field}
                        value={getFieldValue(field)}
                        handleFieldChange={handleFieldChange}
                        conditional={false}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        fields={fields}
                        caseId={leadData?.id}
                        leadId={leadData?.id}
                        refreshAfterCalc={refreshAfterCalc}
                        handleNotesChange={handleNotesChange}
                        notesData={notesData}
                    />
                );
                
                const childrenEls = visibleChildren.map(child => (
                    <Field
                        key={`${child.id}-${index}-child`}
                        field={child}
                        value={getFieldValue(child)}
                        handleFieldChange={handleFieldChange}
                        conditional={true}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        fields={fields}
                        caseId={leadData?.id}
                        leadId={leadData?.id}
                        refreshAfterCalc={refreshAfterCalc}
                        handleNotesChange={handleNotesChange}
                        notesData={notesData}
                    />
                ));

                rendered.add(field.id);
                
                if (visibleChildren.length > 0) {
                    children.forEach(child => {
                        if (shouldDisplay(child)) {
                            rendered.add(child.id);
                        }
                    });
                }

                if (field.field_id === 13) {
                    flushInstructionGroup();
                    flushSubSection();
                    currentSubSection.push(fieldEl, ...childrenEls);
                    flushSubSection();
                } else if (field.field_id === 14) {
                    flushSubSection();
                    currentInstructionGroup.push(fieldEl, ...childrenEls);
                } else {
                    flushInstructionGroup();
                    currentSubSection.push(fieldEl, ...childrenEls);
                }
            });

        flushSubSection();
        flushInstructionGroup();

        return output;
    };

    return (
        <div className='case-section'>
            <div className='toc-header'>
                <h2>{sectionName}</h2>
                {(dataChanged || notesChanged) && (
                    <div className='toc-header-actions'>
                        <button className='action' onClick={saveFields}>Save PFS</button>
                    </div>
                )}
            </div>
            {loading ? (
                <div>Loading PFS fields...</div>
            ) : fields.length > 0 ? (
                <div className="pfs-fields-container">
                    {renderFields()}
                </div>
            ) : (
                <div className="pfs-info">No PFS fields found for this lead</div>
            )}
            {alertMessage && <Alert message={alertMessage.message} type={alertMessage.type} onClose={() => setAlertMessage(null)} />}
        </div>
    );
};
